/**
 * Страница управления чёрным списком (ЧС)
 * Дата: 13.02.2026
 * Обновлено: 14.02.2026 — Рефакторинг стилей на стандартные UI компоненты (light theme)
 * 
 * Функционал:
 * - Просмотр списка пациентов в ЧС по текущему тенанту
 * - Добавление в ЧС по номеру телефона
 * - Удаление из ЧС
 * - Поиск по телефону/pcode
 * - Индикация источника (notifier/segments)
 */
import { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { ShieldOff, Plus, Trash2, RefreshCw, Phone, AlertTriangle } from 'lucide-react';
import { listBlacklist, createBlacklistEntry, removeBlacklistEntry, BlacklistEntry } from '@/api/blacklist';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { SearchInput } from '@/components/ui/SearchInput';
import { TenantSelector } from '@/components/ui/TenantSelector';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

export default function BlacklistPage() {
  const { user } = useAuth();
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const setSelectedTenant = useAuthStore((s) => s.setSelectedTenant);
  const isSuperAdmin = !user?.tenant_id;
  const tenantId = isSuperAdmin ? selectedTenantId : user?.tenant_id ?? undefined;

  const [entries, setEntries] = useState<BlacklistEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const { showSuccess, showError } = useToast();

  // Форма добавления
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newReason, setNewReason] = useState('');
  const [newPcode, setNewPcode] = useState('');
  const [adding, setAdding] = useState(false);

  // Диалог удаления
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; phone: string } | null>(null);

  const fetchData = useCallback(async () => {
    if (!tenantId && isSuperAdmin) return;
    setLoading(true);
    try {
      const data = await listBlacklist({
        tenant_id: tenantId || undefined,
        search: search || undefined,
        limit,
        offset,
      });
      setEntries(data.entries || []);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load blacklist:', err);
      showError('Ошибка загрузки чёрного списка');
    } finally {
      setLoading(false);
    }
  }, [search, offset, tenantId, isSuperAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setOffset(0);
  }, []);

  const handleAdd = async () => {
    if (!newPhone.trim()) {
      showError('Введите номер телефона');
      return;
    }
    setAdding(true);
    try {
      await createBlacklistEntry({
        tenant_id: tenantId || undefined,
        phone: newPhone.replace(/[^\d+]/g, ''),
        pcode: newPcode || undefined,
        reason: newReason || undefined,
        for_broadcasts: true,
      });
      showSuccess('Пациент добавлен в ЧС');
      setNewPhone('');
      setNewReason('');
      setNewPcode('');
      setShowAddForm(false);
      fetchData();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Ошибка добавления';
      showError(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async () => {
    if (!deleteTarget) return;
    try {
      await removeBlacklistEntry(deleteTarget.id);
      showSuccess('Удалено из ЧС');
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      showError('Ошибка удаления');
    }
  };

  return (
    <MainLayout
      headerTitle="Чёрный список"
      headerActions={
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="md"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Добавить в ЧС
          </Button>
          <Button
            variant="ghost"
            size="md"
            onClick={fetchData}
            title="Обновить"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {isSuperAdmin && (
          <TenantSelector
            value={selectedTenantId ?? ''}
            onChange={(id) => setSelectedTenant(id || null)}
          />
        )}

        {/* Warning banner */}
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              Пациентам из чёрного списка не будут отправляться рассылки, напоминания и шаблоны.
            </p>
            <p className="text-xs text-amber-600 mt-1">
              BlacklistConstraint проверяется первым в цепочке constraints — до QuietHours, FrequencyCap, Cooldown, Duplicate.
            </p>
          </div>
        </div>

        {/* Add form */}
        {showAddForm && (
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-neutral-900 mb-3">Добавить в чёрный список</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-neutral-600 mb-1 block">Телефон *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 w-4 h-4 text-neutral-400" />
                  <input
                    type="text"
                    value={newPhone}
                    onChange={e => setNewPhone(e.target.value)}
                    placeholder="+7 (926) 123-45-67"
                    className="w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 mb-1 block">Pcode (код пациента)</label>
                <input
                  type="text"
                  value={newPcode}
                  onChange={e => setNewPcode(e.target.value)}
                  placeholder="10003809"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 mb-1 block">Причина</label>
                <input
                  type="text"
                  value={newReason}
                  onChange={e => setNewReason(e.target.value)}
                  placeholder="Причина добавления в ЧС"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button
                variant="primary"
                size="md"
                onClick={handleAdd}
                isLoading={adding}
              >
                Добавить
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={() => setShowAddForm(false)}
              >
                Отмена
              </Button>
            </div>
          </Card>
        )}

        {/* Search */}
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          placeholder="Поиск по телефону или pcode..."
          size="md"
          showClear
        />

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-neutral-600">
          Всего в ЧС: <strong className="text-neutral-900">{total}</strong>
        </div>

        {/* Table */}
        <Card className="overflow-hidden p-0">
          <Table>
            <TableHead>
              <tr>
                <TableHeader>Телефон</TableHeader>
                <TableHeader>Pcode</TableHeader>
                <TableHeader>Причина</TableHeader>
                <TableHeader>Источник</TableHeader>
                <TableHeader>Рассылки</TableHeader>
                <TableHeader>Дата</TableHeader>
                <TableHeader className="text-right">Действия</TableHeader>
              </tr>
            </TableHead>
            <TableBody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-neutral-500">
                    Загрузка...
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      type="default"
                      title="Чёрный список пуст"
                      description="Пациенты в чёрном списке не получают рассылки и напоминания"
                      icon={<ShieldOff className="w-12 h-12 text-neutral-300" />}
                    />
                  </td>
                </tr>
              ) : (
                entries.map(entry => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <span className="font-mono font-medium text-neutral-900">{entry.phone}</span>
                    </TableCell>
                    <TableCell className="text-neutral-500">{entry.pcode || '—'}</TableCell>
                    <TableCell className="max-w-xs truncate text-neutral-600">
                      <span title={entry.reason || ''}>{entry.reason || '—'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={entry.source_system === 'segments' ? 'info' : 'secondary'}>
                        {entry.source_system}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {entry.for_broadcasts ? (
                        <Badge variant="error">Заблокирован</Badge>
                      ) : (
                        <Badge variant="success">Разрешён</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-neutral-500 text-xs">
                      {new Date(entry.added_at).toLocaleDateString('ru-RU')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget({ id: entry.id, phone: entry.phone })}
                        title="Удалить из ЧС"
                        className="text-neutral-400 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Pagination */}
        <Pagination
          offset={offset}
          limit={limit}
          total={total}
          onOffsetChange={setOffset}
          showInfo
        />

        {/* Confirm delete dialog */}
        <ConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleRemove}
          title="Удалить из чёрного списка?"
          message={`Пациент ${deleteTarget?.phone || ''} будет удалён из чёрного списка и сможет получать рассылки.`}
          confirmText="Удалить"
          cancelText="Отмена"
          variant="danger"
        />
      </div>
    </MainLayout>
  );
}
